import { apiClient } from './api';
import {
  SLAPolicy,
  EscalationRule,
  BusinessHoursCalendar,
  SLAPerformance,
  SLAEvent,
  CreateSLAPolicyRequest,
  UpdateSLAPolicyRequest,
  CreateEscalationRuleRequest,
  UpdateEscalationRuleRequest,
  CreateBusinessHoursCalendarRequest,
  SLADashboardData,
  SLABreachAlert,
  SLAValidationResult,
  SLAPerformanceReport,
  SLAReportGrouping,
  SLAMetricType,
  TicketPriority,
  SLATier,
  SLAStatus,
  EscalationTriggerType,
  EscalationSchedule,
  Holiday,
  HolidayType,
  RecurrencePattern
} from '../types/sla';

/**
 * Service for SLA (Service Level Agreement) management
 * Provides complete CRUD operations and monitoring capabilities
 */
class SLAService {
  private readonly baseUrl = '/sla';

  // ==========================================
  // SLA POLICY MANAGEMENT
  // ==========================================

  /**
   * Get all SLA policies
   */
  async getAllPolicies(): Promise<SLAPolicy[]> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/policies`);
      
      // Validate response data
      if (!Array.isArray(response.data)) {
        throw new Error('SLA data response is invalid');
      }
      
      return response.data;
    } catch (error: any) {
      console.error('Error fetching SLA policies:', error?.message || error);
      
      // Provide more specific error information
      if (error?.response?.status === 401) {
        throw new Error('Authentication required to access SLA policies');
      } else if (error?.response?.status === 403) {
        throw new Error('Insufficient permissions to access SLA policies');
      } else if (error?.code === 'NETWORK_ERROR' || error?.message === 'Network Error') {
        throw new Error('Network error: Unable to connect to SLA service');
      }
      
      throw error;
    }
  }

  /**
   * Get SLA policy by ID
   */
  async getPolicyById(id: string): Promise<SLAPolicy> {
    const response = await apiClient.get(`${this.baseUrl}/policies/${id}`);
    return response.data;
  }

  /**
   * Create new SLA policy
   */
  async createPolicy(request: CreateSLAPolicyRequest): Promise<SLAPolicy> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/policies`, request);
      return response.data;
    } catch (error) {
      console.error('Error creating SLA policy:', error);
      throw error;
    }
  }

  /**
   * Update existing SLA policy
   */
  async updatePolicy(id: string, request: UpdateSLAPolicyRequest): Promise<SLAPolicy> {
    const response = await apiClient.put(`${this.baseUrl}/policies/${id}`, request);
    return response.data;
  }

  /**
   * Delete SLA policy
   */
  async deletePolicy(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/policies/${id}`);
  }

  /**
   * Set policy as default
   */
  async setDefaultPolicy(id: string): Promise<SLAPolicy> {
    const response = await apiClient.post(`${this.baseUrl}/policies/${id}/set-default`);
    return response.data;
  }

  /**
   * Validate SLA policy configuration
   */
  async validatePolicy(policy: SLAPolicy): Promise<SLAValidationResult> {
    const response = await apiClient.post(`${this.baseUrl}/validate-policy`, policy);
    return response.data;
  }

  // ==========================================
  // ESCALATION RULE MANAGEMENT  
  // ==========================================

  /**
   * Get escalation rules for a policy
   */
  async getEscalationRules(policyId: string): Promise<EscalationRule[]> {
    const response = await apiClient.get(`${this.baseUrl}/policies/${policyId}/escalation-rules`);
    return response.data;
  }

  /**
   * Get escalation rule by ID
   */
  async getEscalationRule(id: string): Promise<EscalationRule> {
    const response = await apiClient.get(`${this.baseUrl}/escalation-rules/${id}`);
    return response.data;
  }

  /**
   * Create escalation rule
   */
  async createEscalationRule(policyId: string, request: CreateEscalationRuleRequest): Promise<EscalationRule> {
    const response = await apiClient.post(`${this.baseUrl}/policies/${policyId}/escalation-rules`, request);
    return response.data;
  }

  /**
   * Update escalation rule
   */
  async updateEscalationRule(id: string, request: UpdateEscalationRuleRequest): Promise<EscalationRule> {
    const response = await apiClient.put(`${this.baseUrl}/escalation-rules/${id}`, request);
    return response.data;
  }

  /**
   * Delete escalation rule
   */
  async deleteEscalationRule(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/escalation-rules/${id}`);
  }

  // ==========================================
  // BUSINESS HOURS MANAGEMENT
  // ==========================================

  /**
   * Get all business hours calendars
   */
  async getBusinessHoursCalendars(): Promise<BusinessHoursCalendar[]> {
    const response = await apiClient.get(`${this.baseUrl}/business-hours-calendars`);
    return response.data;
  }

  /**
   * Get business hours calendar by ID
   */
  async getBusinessHoursCalendar(id: string): Promise<BusinessHoursCalendar> {
    const response = await apiClient.get(`${this.baseUrl}/business-hours-calendars/${id}`);
    return response.data;
  }

  /**
   * Create business hours calendar
   */
  async createBusinessHoursCalendar(request: CreateBusinessHoursCalendarRequest): Promise<BusinessHoursCalendar> {
    const response = await apiClient.post(`${this.baseUrl}/business-hours-calendars`, request);
    return response.data;
  }

  /**
   * Update business hours calendar
   */
  async updateBusinessHoursCalendar(id: string, request: CreateBusinessHoursCalendarRequest): Promise<BusinessHoursCalendar> {
    const response = await apiClient.put(`${this.baseUrl}/business-hours-calendars/${id}`, request);
    return response.data;
  }

  /**
   * Delete business hours calendar
   */
  async deleteBusinessHoursCalendar(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/business-hours-calendars/${id}`);
  }

  /**
   * Add holiday to calendar
   */
  async addHoliday(calendarId: string, holiday: Partial<Holiday>): Promise<Holiday> {
    const response = await apiClient.post(`${this.baseUrl}/business-hours-calendars/${calendarId}/holidays`, holiday);
    return response.data;
  }

  /**
   * Update holiday
   */
  async updateHoliday(calendarId: string, holidayId: string, holiday: Partial<Holiday>): Promise<Holiday> {
    const response = await apiClient.put(`${this.baseUrl}/business-hours-calendars/${calendarId}/holidays/${holidayId}`, holiday);
    return response.data;
  }

  /**
   * Delete holiday
   */
  async deleteHoliday(calendarId: string, holidayId: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/business-hours-calendars/${calendarId}/holidays/${holidayId}`);
  }

  // ==========================================
  // SLA MONITORING & ALERTS
  // ==========================================

  /**
   * Get SLA dashboard data
   */
  async getDashboardData(): Promise<SLADashboardData> {
    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);
    const params = new URLSearchParams({ page: '1', pageSize: '1' });
    ['New', 'Open', 'Pending'].forEach(status => params.append('Statuses', status));
    const [policies, warnings, breaches, compliance, tickets] = await Promise.all([
      this.getAllPolicies(), this.getTicketsApproachingBreach(30), this.getBreachedTickets(),
      this.getComplianceRate(startDate, endDate),
      apiClient.get<{ totalItems: number }>(`/tickets?${params.toString()}`)
    ]);
    if (typeof tickets.data?.totalItems !== 'number') throw new Error('Ticket count is unavailable');
    const rate = compliance.complianceRate;
    if (rate !== null && (typeof rate !== 'number' || !Number.isFinite(rate) || rate < 0 || rate > 100)) {
      throw new Error('SLA compliance data is invalid');
    }
    return {
      totalActivePolicies: policies.filter(policy => policy.isActive).length,
      totalActiveTickets: tickets.data.totalItems,
      overallComplianceRate: rate,
      ticketsAtRisk: warnings.length, ticketsBreached: breaches.length,
      breachAlerts: [...warnings, ...breaches], complianceByPriority: {}, recentActivity: [], isPartialData: false
    };
  }

  /**
   * Get SLA status for a ticket
   */
  async getTicketSLAStatus(ticketId: string): Promise<SLAPerformance[]> {
    const response = await apiClient.get(`${this.baseUrl}/tickets/${ticketId}/status`);
    return response.data;
  }

  /**
   * Get tickets approaching SLA breach
   */
  async getTicketsApproachingBreach(warningThresholdMinutes: number = 30, metricType?: SLAMetricType): Promise<SLABreachAlert[]> {
    try {
      const params = new URLSearchParams({
        warningThresholdMinutes: warningThresholdMinutes.toString()
      });
      
      if (metricType !== undefined) {
        params.append('metricType', metricType.toString());
      }

      const response = await apiClient.get(`${this.baseUrl}/breach-warnings?${params.toString()}`);
      
      // Validate response data
      if (!Array.isArray(response.data)) {
        throw new Error('SLA data response is invalid');
      }
      
      return response.data;
    } catch (error: any) {
      console.error('Error fetching tickets approaching breach:', error?.message || error);
      
      // Provide specific error context
      if (error?.response?.status === 401) {
        throw new Error('Authentication required to access breach warnings');
      } else if (error?.code === 'NETWORK_ERROR' || error?.message === 'Network Error') {
        throw new Error('Network error: Unable to connect to breach warning service');
      }
      
      throw error;
    }
  }

  /**
   * Get tickets with SLA breaches
   */
  async getBreachedTickets(metricType?: SLAMetricType, since?: Date): Promise<SLABreachAlert[]> {
    try {
      const params = new URLSearchParams();
      
      if (metricType !== undefined) {
        params.append('metricType', metricType.toString());
      }
      
      if (since) {
        params.append('since', since.toISOString());
      }

      const response = await apiClient.get(`${this.baseUrl}/breached-tickets?${params.toString()}`);
      
      // Validate response data
      if (!Array.isArray(response.data)) {
        throw new Error('SLA data response is invalid');
      }
      
      return response.data;
    } catch (error: any) {
      console.error('Error fetching breached tickets:', error?.message || error);
      
      // Provide specific error context
      if (error?.response?.status === 401) {
        throw new Error('Authentication required to access breached tickets');
      } else if (error?.code === 'NETWORK_ERROR' || error?.message === 'Network Error') {
        throw new Error('Network error: Unable to connect to breached tickets service');
      }
      
      throw error;
    }
  }

  /**
   * Get SLA compliance rate
   */
  async getComplianceRate(
    startDate: Date,
    endDate: Date,
    metricType?: SLAMetricType,
    priority?: TicketPriority
  ): Promise<{ complianceRate: number | null; startDate: string; endDate: string; metricType?: SLAMetricType; priority?: TicketPriority }> {
    const params = new URLSearchParams({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    });

    if (metricType !== undefined) {
      params.append('metricType', metricType.toString());
    }

    if (priority !== undefined) {
      params.append('priority', priority.toString());
    }

    const response = await apiClient.get(`${this.baseUrl}/compliance-rate?${params.toString()}`);
    return response.data;
  }

  /**
   * Get SLA performance report
   */
  async getPerformanceReport(
    startDate: Date,
    endDate: Date,
    groupBy: SLAReportGrouping = SLAReportGrouping.Priority
  ): Promise<SLAPerformanceReport> {
    const params = new URLSearchParams({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      groupBy: groupBy.toString()
    });

    const response = await apiClient.get(`${this.baseUrl}/performance-report?${params.toString()}`);
    return response.data;
  }

  // ==========================================
  // TICKET SLA OPERATIONS
  // ==========================================

  /**
   * Pause SLA for a ticket
   */
  async pauseTicketSLA(ticketId: string, reason: string): Promise<void> {
    await apiClient.post(`${this.baseUrl}/tickets/${ticketId}/pause`, { reason });
  }

  /**
   * Resume SLA for a ticket
   */
  async resumeTicketSLA(ticketId: string): Promise<void> {
    await apiClient.post(`${this.baseUrl}/tickets/${ticketId}/resume`);
  }

  /**
   * Get ticket SLA performance history
   */
  async getTicketSLAPerformance(ticketId: string): Promise<SLAPerformance[]> {
    const response = await apiClient.get(`${this.baseUrl}/tickets/${ticketId}/performance`);
    return response.data;
  }

  // ==========================================
  // ADMIN OPERATIONS
  // ==========================================

  /**
   * Recalculate SLA for all active tickets
   */
  async recalculateAllSLAs(): Promise<{ message: string; processedCount: number }> {
    const response = await apiClient.post(`${this.baseUrl}/recalculate-all`);
    return response.data;
  }

  // ==========================================
  // UTILITY FUNCTIONS
  // ==========================================

  /**
   * Format time in minutes to human-readable string
   */
  formatTimeRemaining(minutes: number): string {
    if (minutes < 0) {
      const overdue = Math.abs(minutes);
      if (overdue < 60) {
        return `${overdue}m overdue`;
      } else if (overdue < 1440) {
        const hours = Math.floor(overdue / 60);
        const mins = overdue % 60;
        return mins > 0 ? `${hours}h ${mins}m overdue` : `${hours}h overdue`;
      } else {
        const days = Math.floor(overdue / 1440);
        const hours = Math.floor((overdue % 1440) / 60);
        return hours > 0 ? `${days}d ${hours}h overdue` : `${days}d overdue`;
      }
    }

    if (minutes < 60) {
      return `${minutes}m`;
    } else if (minutes < 1440) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    } else {
      const days = Math.floor(minutes / 1440);
      const hours = Math.floor((minutes % 1440) / 60);
      return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
    }
  }

  /**
   * Get warning level based on time remaining
   */
  getWarningLevel(timeRemaining: number, targetMinutes: number): 'normal' | 'warning' | 'critical' | 'breached' {
    if (timeRemaining < 0) {
      return 'breached';
    }
    
    const percentageRemaining = (timeRemaining / targetMinutes) * 100;
    
    if (percentageRemaining <= 5) {
      return 'critical';
    } else if (percentageRemaining <= 20) {
      return 'warning';
    } else {
      return 'normal';
    }
  }

  /**
   * Get status color for SLA status
   */
  getStatusColor(status: SLAStatus): string {
    switch (status) {
      case SLAStatus.Achieved:
        return 'text-green-600';
      case SLAStatus.Breached:
        return 'text-red-600';
      case SLAStatus.InProgress:
        return 'text-blue-600';
      case SLAStatus.Paused:
        return 'text-yellow-600';
      case SLAStatus.Cancelled:
        return 'text-gray-600';
      default:
        return 'text-gray-500';
    }
  }

  /**
   * Get priority color for ticket priority
   */
  getPriorityColor(priority: TicketPriority): string {
    switch (priority) {
      case TicketPriority.Critical:
        return 'text-red-600 bg-red-50 border-red-200';
      case TicketPriority.Urgent:
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case TicketPriority.High:
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case TicketPriority.Medium:
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case TicketPriority.Low:
        return 'text-gray-600 bg-gray-50 border-gray-200';
      default:
        return 'text-gray-500 bg-gray-50 border-gray-200';
    }
  }

  /**
   * Get SLA tier display name
   */
  getSLATierName(tier: SLATier): string {
    switch (tier) {
      case SLATier.Basic:
        return 'Basic';
      case SLATier.Premium:
        return 'Premium';
      case SLATier.Enterprise:
        return 'Enterprise';
      case SLATier.VIP:
        return 'VIP';
      case SLATier.Custom:
        return 'Custom';
      default:
        return 'Unknown';
    }
  }

  /**
   * Get SLA tier color
   */
  getSLATierColor(tier: SLATier): string {
    switch (tier) {
      case SLATier.Basic:
        return 'text-gray-600 bg-gray-100';
      case SLATier.Premium:
        return 'text-blue-600 bg-blue-100';
      case SLATier.Enterprise:
        return 'text-purple-600 bg-purple-100';
      case SLATier.VIP:
        return 'text-yellow-600 bg-yellow-100';
      case SLATier.Custom:
        return 'text-indigo-600 bg-indigo-100';
      default:
        return 'text-gray-500 bg-gray-50';
    }
  }

  /**
   * Get escalation trigger type display name
   */
  getTriggerTypeName(triggerType: EscalationTriggerType): string {
    switch (triggerType) {
      case EscalationTriggerType.TimeBeforeBreach:
        return 'Time Before Breach';
      case EscalationTriggerType.PercentageElapsed:
        return 'Percentage Elapsed';
      case EscalationTriggerType.OnBreach:
        return 'On Breach';
      case EscalationTriggerType.NoActivity:
        return 'No Activity';
      case EscalationTriggerType.CustomCondition:
        return 'Custom Condition';
      case EscalationTriggerType.AgentWorkload:
        return 'Agent Workload';
      default:
        return 'Unknown';
    }
  }

  /**
   * Get escalation schedule display name
   */
  getScheduleName(schedule: EscalationSchedule): string {
    switch (schedule) {
      case EscalationSchedule.Always:
        return 'Always';
      case EscalationSchedule.BusinessHoursOnly:
        return 'Business Hours Only';
      case EscalationSchedule.OutsideBusinessHours:
        return 'Outside Business Hours';
      case EscalationSchedule.WeekdaysOnly:
        return 'Weekdays Only';
      case EscalationSchedule.WeekendsOnly:
        return 'Weekends Only';
      case EscalationSchedule.Custom:
        return 'Custom Schedule';
      default:
        return 'Unknown';
    }
  }

  /**
   * Get holiday type display name
   */
  getHolidayTypeName(type: HolidayType): string {
    switch (type) {
      case HolidayType.National:
        return 'National';
      case HolidayType.Company:
        return 'Company';
      case HolidayType.Regional:
        return 'Regional';
      case HolidayType.Religious:
        return 'Religious';
      case HolidayType.Cultural:
        return 'Cultural';
      case HolidayType.Emergency:
        return 'Emergency';
      default:
        return 'Unknown';
    }
  }

  /**
   * Get recurrence pattern display name
   */
  getRecurrencePatternName(pattern: RecurrencePattern): string {
    switch (pattern) {
      case RecurrencePattern.Yearly:
        return 'Yearly';
      case RecurrencePattern.Monthly:
        return 'Monthly';
      case RecurrencePattern.WeeklyPattern:
        return 'Weekly Pattern';
      case RecurrencePattern.Custom:
        return 'Custom';
      default:
        return 'None';
    }
  }

  /**
   * Parse time span string to hours and minutes
   */
  parseTimeSpan(timeSpan: string): { hours: number; minutes: number } {
    const parts = timeSpan.split(':');
    return {
      hours: parseInt(parts[0], 10) || 0,
      minutes: parseInt(parts[1], 10) || 0
    };
  }

  /**
   * Format hours and minutes to time span string
   */
  formatTimeSpan(hours: number, minutes: number): string {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
  }

  /**
   * Convert minutes to hours and minutes display
   */
  minutesToHoursMinutes(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours === 0) {
      return `${mins}m`;
    } else if (mins === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${mins}m`;
    }
  }

  /**
   * Get business days array from comma-separated string
   */
  parseBusinessDays(businessDays: string): string[] {
    return businessDays.split(',').map(day => day.trim());
  }

  /**
   * Format business days array to comma-separated string
   */
  formatBusinessDays(days: string[]): string {
    return days.join(',');
  }

  /**
   * Check if current time is within business hours for a policy
   */
  isWithinBusinessHours(policy: SLAPolicy, currentTime: Date = new Date()): boolean {
    if (!policy.businessHoursOnly) {
      return true;
    }

    const businessDays = this.parseBusinessDays(policy.businessDays);
    const currentDay = currentTime.toLocaleDateString('en-US', { weekday: 'long' });
    
    if (!businessDays.includes(currentDay)) {
      return false;
    }

    const currentTimeSpan = this.formatTimeSpan(currentTime.getHours(), currentTime.getMinutes());
    return currentTimeSpan >= policy.businessHoursStart && currentTimeSpan <= policy.businessHoursEnd;
  }
}

export const slaService = new SLAService();
export default slaService;